import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon, UserIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Client, decorateClient } from '@/db/types/client';
import { titleize } from '../lib/inflection';

const PAGE_SIZE = 25;

type ClientsListProps = {
  clients: Client[];
  onCantFindClient?: () => void;
  onNewDocument?: (clientId: number, clientSlug: string) => void;
};

export default function ClientsList({ clients, onCantFindClient, onNewDocument }: ClientsListProps) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const decoratedClients = useMemo(() => clients.map(decorateClient), [clients]);

  const filteredClients = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return decoratedClients;
    return decoratedClients.filter(client =>
      client.person.displayName.toLowerCase().includes(term) ||
      client.label.toLowerCase().includes(term)
    );
  }, [decoratedClients, searchTerm]);

  // Reset to page 1 when search changes
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedClients = filteredClients.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    paused: 'bg-yellow-100 text-yellow-800',
    closed: 'bg-red-100 text-red-800',
  };

  return (
    <>
      {/* Search bar — full width, top */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-20 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-deep-sea-green focus:border-deep-sea-green"
          placeholder="Search by name or ID"
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') handleSearch(''); }}
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center gap-1.5">
          {searchTerm && (
            <button
              type="button"
              onClick={() => handleSearch('')}
              className="p-0.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              aria-label="Clear search"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
          <span className="pointer-events-none">
            <kbd className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 bg-gray-100 rounded border border-gray-200">
              ⌘K
            </kbd>
          </span>
        </div>
      </div>

      {/* Heading row */}
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl text-black">My Clients</h1>
        {onCantFindClient && (
          <button
            onClick={onCantFindClient}
            className="text-sm underline decoration-dotted decoration-2 decoration-gray-400 underline-offset-2 hover:decoration-gray-600 hover:text-gray-700 transition-colors"
          >
            Can't find the client you are looking for?
          </button>
        )}
      </div>

      {/* Table */}
      {filteredClients.length > 0 ? (
        <>
          <div className="border border-[#ddd] rounded overflow-hidden">
            {/* Header */}
            <div className="bg-[#f9f9f9] border-b border-[#ddd] grid grid-cols-[1fr_100px_180px_150px] items-center h-11 px-3 text-xs font-semibold text-gray-900 uppercase tracking-wider">
              <span>Client</span>
              <span>Status</span>
              <span>Last updated</span>
              <span>Action</span>
            </div>

            {/* Rows */}
            {paginatedClients.map((client) => (
                <div
                  key={client.id}
                  className="grid grid-cols-[1fr_100px_180px_150px] items-center min-h-[44px] px-3 border-b border-[#eee] last:border-b-0 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/clients/${client.label}`, { state: { from: '/clients' } })}
                >
                  {/* Client name */}
                  <div className="flex items-center gap-2 py-2">
                    <UserIcon className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {client.person.displayName}
                    </span>
                  </div>

                  {/* Status */}
                  <div>
                    <span className={`inline-flex items-center text-xs font-semibold px-1.5 py-0.5 rounded ${statusColors[client.status?.toLowerCase()] || 'bg-gray-100 text-gray-800'}`}>
                      {titleize(client.status || 'unknown')}
                    </span>
                  </div>

                  {/* Latest assigned date */}
                  <span className="text-xs text-gray-600">
                    {client.updated_at ? formatDate(client.updated_at) : '—'}
                  </span>

                  {/* Action */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => onNewDocument?.(client.id, client.label)}
                      className="inline-flex items-center gap-1 text-xs font-bold text-gray-700 hover:text-black px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                    >
                      <PlusIcon className="w-3 h-3" strokeWidth={2.5} />
                      New Document
                    </button>
                  </div>
                </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs text-gray-500">
                Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filteredClients.length)} of {filteredClients.length}
              </span>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="px-2 py-1 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {generatePageNumbers(safePage, totalPages).map((page, i) =>
                  page === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-1.5 text-xs text-gray-400">...</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page as number)}
                      className={`px-2.5 py-1 text-xs font-medium rounded border ${
                        page === safePage
                          ? 'bg-deep-sea-green text-white border-deep-sea-green'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="px-2 py-1 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="py-16 text-center text-gray-500">
          {searchTerm ? (
            <>
              <p className="text-sm font-medium mb-1">No clients found</p>
              <p className="text-xs">Try adjusting your search terms</p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium mb-1">No clients yet</p>
              <p className="text-xs">Get started by adding your first client</p>
            </>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Generate page numbers with ellipsis for large page counts.
 * Always shows first, last, and up to 2 pages around current.
 */
function generatePageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | '...')[] = [];
  const near = new Set([1, 2, current - 1, current, current + 1, total - 1, total]);

  let prev = 0;
  for (const p of [...near].sort((a, b) => a - b)) {
    if (p < 1 || p > total) continue;
    if (prev && p - prev > 1) pages.push('...');
    pages.push(p);
    prev = p;
  }
  return pages;
}
